import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["turnstile"]

  connect() {
    if (!this.hasTurnstileTarget) return

    this._disableSubmitButton()
    this._setTurnstileInteractive(false)

    if (!window.turnstile?.render) {
      this._waitForTurnstile()
      return
    }

    this._renderTurnstile()
  }

  disconnect() {
    clearInterval(this.turnstileWaitInterval)
    clearTimeout(this.turnstileWaitTimeout)
    window.turnstile?.remove(this.widgetId)
  }

  _waitForTurnstile() {
    this.turnstileWaitInterval = setInterval(() => {
      if (!window.turnstile?.render) return

      clearInterval(this.turnstileWaitInterval)
      clearTimeout(this.turnstileWaitTimeout)
      this._renderTurnstile()
    }, 250)

    this.turnstileWaitTimeout = setTimeout(() => {
      clearInterval(this.turnstileWaitInterval)
      this._disableSubmitButton("Verification failed, please refresh")
      this._reportRenderFailure("turnstile_not_loaded")
    }, 15000)
  }

  _renderTurnstile() {
    try {
      this.widgetId = window.turnstile.render(this.turnstileTarget, {
        sitekey: this.element.dataset.sitekey,
        size:
          this.element.dataset.size === "" ? null : this.element.dataset.size,
        theme:
          this.element.dataset.theme === "" ? null : this.element.dataset.theme,
        appearance:
          this.element.dataset.appearance === ""
            ? null
            : this.element.dataset.appearance,
        callback: () => {
          this._enableSubmitButton()
        },
        "error-callback": (errorCode) => {
          this._handleErrorCallback(errorCode)
          this._disableSubmitButton()
          this._resetTurnstile()
        },
        "expired-callback": () => {
          this._disableSubmitButton()
          this._resetTurnstile()
        },
        "before-interactive-callback": () => {
          this._setTurnstileInteractive(true)
        },
      })
    } catch (error) {
      this._disableSubmitButton("Verification failed, please refresh")
      this._reportRenderFailure("turnstile_render_exception", error)
    }
  }

  _setTurnstileInteractive(interactive) {
    if (!this.hasTurnstileTarget) return

    this.turnstileTarget.classList.toggle("hide", !interactive)

    if (interactive) {
      this.turnstileTarget.removeAttribute("inert")
      this.turnstileTarget.removeAttribute("aria-hidden")
    } else {
      this.turnstileTarget.setAttribute("inert", "")
      this.turnstileTarget.setAttribute("aria-hidden", "true")
    }
  }

  _handleErrorCallback(errorCode) {
    const errorFamily = Math.floor(errorCode / 1000)

    switch(errorFamily) {
      case 100:
        console.error('Please refresh the page and try again. Make sure Cloudflare JavaScript is not blocked.')
        break
      case 110:
        _reportRenderFailure('Configuration error. Please contact support.', errorCode)
        break
      case 300:
      case 600:
        console.error('Security check failed. Please try refreshing or unblocking Cloudflare JavaScript.')
        break
      default:
        _reportRenderFailure('An unexpected error occurred. Please try again.', errorCode)
    }
  }

  _reportRenderFailure(reason, error = null) {
    if (this.hasReportedRenderFailure || !window.appsignal?.sendError) return

    this.hasReportedRenderFailure = true

    const errorToReport =
      error || new Error(`Turnstile render failure: ${reason}`)

    window.appsignal.sendError(errorToReport, (span) => {
      span.setAction(window.location.pathname)
      span.setTags({
        source: "turnstile_controller",
        failure_reason: reason,
      })
    })
  }

  _resetTurnstile() {
    if (!window.turnstile?.reset || !this.widgetId) return

    window.turnstile.reset(this.widgetId)
  }

  _enableSubmitButton() {
    let btn = this.closestSubmitButton
    if (btn) {
      btn.value = btn.dataset.turnstilePreviousValue || btn.value
      delete btn.dataset.turnstilePreviousValue
      btn.removeAttribute("disabled")
    }
  }

  _disableSubmitButton(val = "Verifying...") {
    let btn = this.closestSubmitButton
    if (!btn) return

    if (!btn.dataset.turnstilePreviousValue) {
      btn.dataset.turnstilePreviousValue = btn.value
    }

    btn.value = val
    btn.setAttribute("disabled", "disabled")
  }

  get closestSubmitButton() {
    return this.element.closest("form")?.querySelector("[type='submit']")
  }
}
